<script setup lang="ts">
import { computed } from 'vue';
import SpIcon from './SpIcon.vue';
import type { IconName } from '../icons';

/**
 * The button.
 *
 * Two behaviours are deliberate rather than incidental:
 *
 * - **A loading button keeps its width.** The label stays in the DOM at zero opacity
 *   with the spinner overlaid, so the layout does not jump when an action starts.
 *   A row of buttons that reflows on click is how a user mis-clicks the next one.
 * - **A loading button is disabled.** Double-submitting a pod creation is not merely
 *   untidy; it bills twice.
 */
const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
    size?: 'sm' | 'md' | 'lg';
    icon?: IconName;
    iconRight?: IconName;
    loading?: boolean;
    disabled?: boolean;
    block?: boolean;
    type?: 'button' | 'submit' | 'reset';
    /** Required when the button has no visible text. */
    label?: string;
  }>(),
  { variant: 'secondary', size: 'md', type: 'button' },
);

const emit = defineEmits<{ click: [MouseEvent] }>();

const inert = computed(() => props.disabled === true || props.loading === true);

const VARIANTS = {
  primary:
    'bg-accent text-accent-contrast border-transparent hover:bg-accent-hover active:bg-accent-active shadow-accent',
  secondary:
    'bg-surface-raised text-ink border-line hover:bg-surface-hover hover:border-line-strong active:bg-surface-active',
  ghost:
    'bg-transparent text-ink-secondary border-transparent hover:bg-surface-hover hover:text-ink',
  danger: 'bg-danger-subtle text-danger border-danger-border hover:bg-danger hover:text-white',
  success:
    'bg-success-subtle text-success border-success-border hover:bg-success hover:text-canvas',
} as const;

const SIZES = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-sm',
  md: 'h-9 px-3.5 text-sm gap-2 rounded',
  lg: 'h-11 px-5 text-base gap-2 rounded-lg',
} as const;

const ICON_SIZE = { sm: 14, md: 16, lg: 18 } as const;
</script>

<template>
  <button
    :type="type"
    :disabled="inert"
    :aria-label="label"
    :aria-busy="loading ? 'true' : undefined"
    class="relative inline-flex items-center justify-center border font-medium tracking-tight transition-all duration-fast select-none disabled:opacity-45 disabled:cursor-not-allowed disabled:shadow-none"
    :class="[VARIANTS[variant], SIZES[size], block ? 'w-full' : '']"
    @click="emit('click', $event)"
  >
    <span v-if="loading" class="absolute inset-0 grid place-items-center" aria-hidden="true">
      <span
        class="h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin"
      />
    </span>
    <!-- Kept in the flow so the button cannot change width while loading. -->
    <span
      class="inline-flex items-center transition-opacity duration-fast"
      :class="[
        loading ? 'opacity-0' : 'opacity-100',
        SIZES[size].includes('gap-1.5') ? 'gap-1.5' : 'gap-2',
      ]"
    >
      <SpIcon v-if="icon" :name="icon" :size="ICON_SIZE[size]" />
      <slot />
      <SpIcon v-if="iconRight" :name="iconRight" :size="ICON_SIZE[size]" />
    </span>
  </button>
</template>
